import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { useIntl } from 'react-intl'
import { Linking, View } from 'react-native'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useBlocker, useLocation, useNavigate } from 'react-router-dom'

import { cloneDeep, get, isEmpty, noop } from 'lodash-es'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'
import styled from 'styled-components/native'

import { AssignmentStatuses } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import {
  assignmentHasCompletedResponse,
  assignmentHasProviderDraft,
  getEffectiveRecipientLyraId,
  getProviderDraftResponse,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { AssignmentResponse } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Curriculum, Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import DefaultButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/defaultButton/DefaultButton'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import { InputFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { SelectFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { ProviderUIMetadataComponent } from '@lyrahealth-inc/ui-core-crossplatform/src/formMetadata/providerUIMetaDataComponent/ProviderUIMetadataComponent'
import { ExerciseConfigDraftItem } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/exerciseConfigDraftItem/ExerciseConfigDraftItem'
import { ExerciseConfigStartDraft } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/exerciseConfigStartDraft/ExerciseConfigStartDraft'
import { BaseModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/baseModal/BaseModal'
import { FullScreenOverlay } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/fullScreenOverlay/FullScreenOverlay'
import { ActivityEntry } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/activityEntry/ActivityEntry'
import { InfoSheet } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/infoSheet/InfoSheet'
import { Lesson } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/lesson/Lesson.web'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

import { AddActivityModal } from './AddActivityModal'
import { getAssignmentAppointmentTiming, getFutureSessions, withCurriculumSession } from './assignmentSessionUtils'
import styles from './configAssignmentContainer.module.scss'
import ConfirmSessionForAssignment from './ConfirmSessionForAssignment'
import { DeleteExerciseDraftModal } from './DeleteExerciseDraftModal'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { programConfig as PROGRAM_CONFIG } from '../../common/constants/appConstants'
import { CLIENT_ACTIVITIES, LT_CLIENTS } from '../../common/constants/routingConstants'
import { getAuthUser } from '../../data/auth/authSelectors'
import { getClientAssignmentResponse, getClientCurrentSessionCount } from '../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsData } from '../../data/lyraTherapy/contentSelectors'
import { RootState, useAppDispatch } from '../../data/store'
import { useVideoSessionBannerHeight } from '../../hooks/useVideoSessionBannerHeight'
import { getAssignmentProviderPayload } from '../assignments/data/assignmentPayload'
import {
  useDeleteAssignmentResponseMutation,
  useSetAssignmentDraftResponseToAssignmentMutation,
  useSetAssignmentMutation,
  useUnsetAssignmentMutation,
  useUpdateAssignmentMutation,
} from '../assignments/data/assignmentsApi'
import { clearResponseDetails, setResponseDetails } from '../assignments/data/assignmentsSlice'
import { useSelectedAssignment } from '../assignments/data/hooks/useSelectedAssignment'
import ProviderExerciseDraft from '../assignments/ProviderExerciseDraft'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import ConfigureCheckIn, { ConfigureCheckInRef } from '../configureCheckIn/ConfigureCheckIn'
import ConfigureMedsInfoSheet, { ConfigureMedsInfoSheetRef } from '../configureMedsInfoSheet/ConfigureMedsInfoSheet'
import { setToastContent } from '../data/ltToastAutoActions'
import { getLTVideoSessionOpen } from '../data/ltVideoSelectors'
import { useUpdateCurriculumMutation } from '../episodes/data/episodesApi'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'
import FormSchemaBuilder, { FormSchemaBuilderRef } from '../formSchemaBuilder/FormSchemaBuilder'
import GroupedFieldsFormSchemaBuilder, {
  GroupedFieldsFormSchemaBuilderRef,
} from '../formSchemaBuilder/GroupedFieldsFormSchemaBuilder'
import MiniBackNav from '../miniBackNav/MiniBackNav'

type ConfigAssignmentContainerProps = ConnectedProps<typeof connector> & {
  actions: any
}

const StartDraftContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  margin: `${theme.spacing['32px']} 0px`,
}))

const ConfigAssignmentContainer: FunctionComponent<ConfigAssignmentContainerProps> = ({
  actions: { setToastContent },
  currentSessionCount,
  contents,
  videoSessionOpen,
  currentAssignmentResponse,
}) => {
  const dispatch = useAppDispatch()
  const currentAssignmentDetails = useSelectedAssignment()
  const user = useSelector(getAuthUser)
  const [deleteAssignmentResponse] = useDeleteAssignmentResponseMutation()
  const [setAssignmentDraftResponseToAssignment] = useSetAssignmentDraftResponseToAssignmentMutation()
  const { data: allAppointments = [] } = useClientAppointmentsData()
  const [updateCurriculum] = useUpdateCurriculumMutation()
  const [setAssignment] = useSetAssignmentMutation()
  const [unsetAssignment] = useUnsetAssignmentMutation()
  const [updateAssignment] = useUpdateAssignmentMutation()
  const { data: clientDetails } = useSelectedClientData()
  const { state } = useLocation()
  const navigate = useNavigate()
  const [addActivityModalOpen, setAddActivityModalOpen] = useState<boolean>(false)

  const { data: clientDetailsSelectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const episodeId = clientDetailsSelectedEpisode?.id
  const assignmentProviderPayload = getAssignmentProviderPayload(clientDetailsSelectedEpisode, user?.id ?? '')
  const curriculum = clientDetailsSelectedEpisode ? clientDetailsSelectedEpisode.curriculum : []

  const programConfig = clientDetailsSelectedEpisode
    ? PROGRAM_CONFIG[clientDetailsSelectedEpisode.program_name]
    : undefined

  const appointments = allAppointments.filter((appt) => appt.episodeId === episodeId)

  // 'content' can be a 'Content' object or an 'Assignment'
  // state is the navigation.state value set in Client.tsx
  const { content, editMode, recipientLyraId: recipientLyraIdFromState, recipientType, sessionCount } = state || {}
  const { formatDate, formatTime } = useIntl()
  const providerDraftResponse = getProviderDraftResponse(content)
  const clientFirstName = clientDetails?.first_name
  const patientRecipientLyraId = clientDetailsSelectedEpisode?.patient_id
  const isDBTTeens = clientDetailsSelectedEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens
  // For DBT Teens, resolves to the teen or caregiver ID depending on which section the provider is working in.
  const normalizedRecipientLyraId = isDBTTeens
    ? content?.recipient_id || recipientLyraIdFromState || patientRecipientLyraId
    : patientRecipientLyraId

  // Fallback set up to handle both cases
  const group = get(content, 'content.group') || content?.group
  const type = get(content, 'content.content_type') || content?.content_type
  const name = get(content, 'content.name') || content?.name
  const metaData =
    get(content, 'content_meta_data') ??
    content?.meta_data ??
    contents.find((content: any) => content.name === name)?.meta_data
  const title = get(content, 'content.title') || content?.title
  const instructions =
    get(content, 'instructions') || contents.find((content: any) => content.name === name)?.instructions
  const contentId = content?.content_id || content?.id || contents.find((content: any) => content.name === name)?.id
  const assignmentId = content?.content_id ? content?.id : undefined
  const assignmentStatus = content?.status

  // We should try to use the assignment response that is in state as this
  // will be the most up to date.
  const selectedAssignmentResponse = !isEmpty(currentAssignmentResponse)
    ? currentAssignmentResponse
    : providerDraftResponse

  const adjustedDate = formatDate(selectedAssignmentResponse?.update_date ?? new Date(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const adjustedTime = formatTime(selectedAssignmentResponse?.update_date ?? new Date())

  const [modalContent, setModalContent] = useState<React.ReactElement | null>(null)
  const [showingPreview, setShowingPreview] = useState<boolean>(false)

  const specificConfig = useRef<ConfigureCheckInRef | ConfigureMedsInfoSheetRef | FormSchemaBuilderRef | null>(null)

  const initialSession = sessionCount <= currentSessionCount ? currentSessionCount : sessionCount
  const initialSessionValue = !programConfig?.canPlanFutureAssignments ? content?.session_period - 1 : initialSession
  const initialValues = {
    session: !isNaN(initialSessionValue) ? initialSessionValue.toString() : '',
    instructions,
  }

  const isCurrentResponseProviderDraft =
    currentAssignmentResponse?.provider_started &&
    currentAssignmentResponse?.status === AssignmentStatuses.provider_draft &&
    !currentAssignmentResponse.assignment_id

  useEffect(() => {
    return () => {
      // if the provider leaves the config assignment, clear the assignment response in the Redux store
      dispatch(clearResponseDetails())
    }
  }, [dispatch])

  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }): boolean => {
    if (
      isCurrentResponseProviderDraft &&
      !currentAssignmentDetails?.id &&
      currentLocation.pathname !== nextLocation.pathname
    ) {
      setAddActivityModalOpen(true)
      return true
    } else {
      return false
    }
  })

  const onPreviewClick = (values: Dict) => {
    track({ event: 'BUTTON_PRESS', action: 'PREVIEW_ACTIVITY' })
    setModalContent(getPreview(values))
    setShowingPreview(true)
  }

  const goClientHome = () => {
    const route = CLIENT_ACTIVITIES.route
    navigate(route, { state: { sessionCount } })
  }

  const navigateAfterAssignmentCreation = () => {
    if (navigationBlocker.state === 'blocked') {
      document.body.style.overflow = 'auto'
      navigationBlocker.proceed()
    } else {
      goClientHome()
    }
  }

  const submitDisabled = (values: Record<string, any>) => {
    let assignmentExists = false
    if (editMode) return false
    if (Number(values.session) > currentSessionCount) {
      const futureCurriculum = curriculum.filter((curr: Curriculum) => curr.session > currentSessionCount)
      const sessionToUpdate = futureCurriculum.findIndex(
        (sessionObject: any) => sessionObject.session === Number(values.session),
      )
      if (sessionToUpdate > -1) {
        assignmentExists = futureCurriculum[sessionToUpdate].contents.some(
          (assignment) =>
            assignment.name === name &&
            getEffectiveRecipientLyraId(assignment.recipient_id, patientRecipientLyraId) === normalizedRecipientLyraId,
        )
      }
    } else if (Number(values.session) === currentSessionCount) {
      // future Assignments logic
    }
    return assignmentExists
  }

  const generateAssignmentData = (values: Dict) => {
    const contentMetadata = specificConfig.current ? specificConfig.current.configure(values) : metaData
    const assignmentContent = cloneDeep(content)
    assignmentContent.meta_data = contentMetadata
    assignmentContent.schema = contentMetadata.schema
    assignmentContent.uiSchema = contentMetadata.uiSchema
    const assignmentInstructions: string = values.instructions as string
    const assignmentName = assignmentContent.name || assignmentContent.content.name

    return {
      assignmentContent,
      assignmentInstructions,
      assignmentName,
    }
  }

  const getPreview = (values: Dict) => {
    const { assignmentContent, assignmentInstructions, assignmentName } = generateAssignmentData(values)

    if (group === 'lesson') {
      return (
        <Lesson
          onBackPress={() => {
            setModalContent(null)
            setShowingPreview(false)
          }}
          contentMetaData={assignmentContent.meta_data}
          content={assignmentContent}
          assignmentId={assignmentContent.id}
          instructions={assignmentInstructions}
        />
      )
    } else if (group === 'infosheet') {
      assignmentContent.instructions = assignmentInstructions
      assignmentContent.title = title
      return (
        <InfoSheet
          title={title}
          instructions={assignmentInstructions}
          metaData={metaData}
          onLinkClicked={(url) => Linking.openURL(url)}
        />
      )
    } else {
      return (
        <ActivityEntry
          contentMetadata={assignmentContent}
          deleteDraft={noop}
          exit={() => {
            setModalContent(null)
            setShowingPreview(false)
          }}
          focused={false}
          instructions={assignmentInstructions}
          name={assignmentName}
          saveForm={noop}
          showAllPages
          title={title}
          withPageBreaks={false}
          isProviderPreview
          videoSessionOpen={videoSessionOpen}
        />
      )
    }
  }

  const renderExerciseDraft = (values: Dict, assignmentResponse?: AssignmentResponse) => {
    const { assignmentContent, assignmentInstructions, assignmentName } = generateAssignmentData(values)

    setShowingPreview(true)
    setModalContent(
      <ProviderExerciseDraft
        assignmentId={assignmentId}
        content={assignmentContent}
        editMode={editMode}
        instructions={assignmentInstructions}
        name={assignmentName}
        onExit={() => {
          setModalContent(null)
          setShowingPreview(false)
        }}
        response={editMode ? selectedAssignmentResponse?.response : assignmentResponse?.response}
        responseId={editMode ? selectedAssignmentResponse?.id : assignmentResponse?.id}
        title={title}
        selectedEpisodeId={episodeId}
        recipientLyraId={normalizedRecipientLyraId}
      />,
    )
  }

  const renderDeleteExerciseDraftModal = () => {
    const onDelete = () => {
      if (assignmentHasProviderDraft(content)) {
        const draftResponse = content?.assignment_responses.find(
          (response: AssignmentResponse) => response.status === 'provider_draft',
        )
        if (draftResponse) {
          deleteAssignmentResponse({ id: draftResponse.id }).unwrap()
          const updatedAssignmentResponse = cloneDeep(content)
          updatedAssignmentResponse.assignment_responses = updatedAssignmentResponse.assignment_responses.filter(
            (response: AssignmentResponse) => {
              return response.status !== 'provider_draft'
            },
          )
          navigate(window.location.pathname, {
            replace: true,
            state: {
              sessionCount,
              editMode,
              content: updatedAssignmentResponse,
              ...(normalizedRecipientLyraId && { recipientLyraId: normalizedRecipientLyraId }),
              ...(recipientType && { recipientType }),
            },
          })
        }
      }
      dispatch(clearResponseDetails())
      setModalContent(null)
      setToastContent({
        text: 'The saved draft has been deleted.',
        id: 'MessageDeleted-Toast-Success',
        toastType: 'success',
      })
    }

    setModalContent(
      <DeleteExerciseDraftModal
        adjustedDate={adjustedDate}
        onCancel={() => setModalContent(null)}
        onDelete={onDelete}
      />,
    )
  }

  const renderConfigView = (form: any, values: Dict) => {
    if (!curriculum) return false
    const futureSessions = getFutureSessions(curriculum, currentSessionCount, programConfig?.canPlanFutureAssignments)
    const showInstructionField = !['medsInfoSheet', 'baseInfoSheet'].includes(name)

    const currentSessionDropdownOption = {
      label: `${programConfig?.sessionLabel} ${
        !programConfig?.canPlanFutureAssignments ? content?.session_period - 1 : currentSessionCount
      }`,
      value: (!programConfig?.canPlanFutureAssignments ? content?.session_period - 1 : currentSessionCount).toString(),
    }

    const sessionDropdownOptions = !editMode
      ? futureSessions.map((session) => {
          return {
            label: `${programConfig?.sessionLabel} ${session}`,
            value: session.toString(),
          }
        })
      : []

    sessionDropdownOptions.unshift(currentSessionDropdownOption)

    return (
      <div className={styles['config-container']}>
        <div className={styles.sessionSelect}>
          <Field
            name='session'
            component={SelectFieldRFF}
            label='Session: '
            options={sessionDropdownOptions}
            readOnly={editMode}
            baseInputStyle={{ marginBottom: 10 }}
            useStringValue
          />
        </div>

        {group === 'exercise' && !assignmentHasCompletedResponse(content) && (
          <StartDraftContainer>
            {!isEmpty(selectedAssignmentResponse) ? (
              <ExerciseConfigDraftItem
                title={adjustedDate}
                timeSubText={adjustedTime}
                clientFirstName={clientFirstName ?? ''}
                renderExerciseDraft={() => renderExerciseDraft(values, selectedAssignmentResponse)}
                onDeleteExerciseDraft={() => renderDeleteExerciseDraftModal()}
                allowActions={selectedAssignmentResponse.status !== 'draft'}
              />
            ) : (
              <ExerciseConfigStartDraft onStartDraftPress={() => renderExerciseDraft(values)} />
            )}
          </StartDraftContainer>
        )}

        {showInstructionField && (
          <Field
            name='instructions'
            label='Instructions'
            placeholder='Enclose links in <> to make them clickable...'
            component={InputFieldRFF}
            multiline
            numberOfLines={10}
          />
        )}
        {renderFormSpecificConfig(form, values)}

        {addActivityModalOpen && navigationBlocker.state === 'blocked' && (
          <AddActivityModal
            onPrimaryButtonPress={() => {
              setAddActivityModalOpen(false)
              validateSelectedSession(values)
            }}
            onRequestClose={() => {
              navigationBlocker.reset()
            }}
            onTertiaryButtonPress={() => navigationBlocker.proceed()}
          />
        )}
      </div>
    )
  }

  const renderFormSpecificConfig = (form: any, formValues: Dict) => {
    const { initialize, restart } = form

    if (['checkIn', 'coachingCheckIn', 'medsCheckIn', 'bctCheckIn', 'bccCheckIn', 'bcmCheckIn'].includes(name)) {
      // CheckIn is a special snowflake
      return (
        <ConfigureCheckIn
          initialize={initialize}
          formValues={formValues}
          metadata={metaData}
          ref={(component: ConfigureCheckInRef) => {
            specificConfig.current = component as ConfigureCheckInRef | null
          }}
          form={form}
        />
      )
    } else if (name === 'medsInfoSheet' || name === 'baseInfoSheet') {
      return (
        <ConfigureMedsInfoSheet
          initialize={initialize}
          formValues={formValues}
          metadata={metaData}
          ref={(component) => {
            specificConfig.current = component as ConfigureMedsInfoSheetRef | null
          }}
          restart={restart}
          form={form}
        />
      )
    } else if (name === 'baseYouthTopProblems' || name === 'bccTopProblems') {
      const fields = get(ProviderUIMetadataComponent, `${name}.customFields.fields`)
      const maxNumberOfFieldGroups = get(ProviderUIMetadataComponent, `${name}.customFields.maxNumberOfFieldGroups`)
      return fields ? (
        <GroupedFieldsFormSchemaBuilder
          constraints={fields}
          maxNumberOfFieldGroups={maxNumberOfFieldGroups || 10}
          metadata={metaData}
          ref={(component) => {
            specificConfig.current = component as GroupedFieldsFormSchemaBuilderRef | null
          }}
          name={name}
          title={title}
        />
      ) : null
    } else {
      const fields = get(ProviderUIMetadataComponent, `${name}.customFields.fields`)
      return fields ? (
        <FormSchemaBuilder
          constraints={fields}
          metadata={metaData}
          ref={(component) => {
            specificConfig.current = component as FormSchemaBuilderRef | null
          }}
          name={name}
          title={title}
        />
      ) : null
    }
  }

  const validateSelectedSession = (values: any) => {
    if (!assignmentProviderPayload) return

    const selectedSession = parseInt(get(values, 'session'))
    const selectedAppointment = appointments.find((appt) => appt.sessionNumber === selectedSession)
    const appointmentToCheck =
      selectedSession === currentSessionCount && programConfig?.shouldConfirmSameSessionAssignment
        ? selectedAppointment
        : undefined
    const appointmentTiming = appointmentToCheck ? getAssignmentAppointmentTiming(appointmentToCheck) : undefined

    if (appointmentTiming?.shouldConfirmSameSession) {
      setModalContent(
        <ConfirmSessionForAssignment
          selectedSession={selectedSession}
          appointmentDate={appointmentTiming.formattedStart}
          assignContent={(session) => assignContent(values, session)}
          showNextSessionOption={!!programConfig?.canPlanFutureAssignments}
        />,
      )
    } else {
      assignContent(values, selectedSession)
    }
  }

  const assignContent = (values: any, selectedSession: number) => {
    if (!assignmentProviderPayload) return

    const contentMetadata = specificConfig.current ? specificConfig.current.configure(values) : metaData
    const isExerciseForFutureSessionWithProviderDraft =
      group === 'exercise' && selectedSession > currentSessionCount && isCurrentResponseProviderDraft

    const sessionPeriod = !programConfig?.canPlanFutureAssignments
      ? content?.session_period
      : isExerciseForFutureSessionWithProviderDraft
        ? selectedSession
        : currentSessionCount

    const data = {
      provider_id: assignmentProviderPayload.provider_id,
      patient_id: clientDetails?.id,
      content_id: contentId,
      content_meta_data: contentMetadata,
      instructions: values.instructions,
      recipient_id: normalizedRecipientLyraId,
      session_period: sessionPeriod,
    }

    let assignmentToUpdate = -1

    const curriculumCopy = withCurriculumSession(curriculum, selectedSession)
    // if the session is the current session, update the assignment data
    if (selectedSession === currentSessionCount || !programConfig?.canPlanFutureAssignments) {
      if (editMode) {
        const assignmentData = {
          id: assignmentId,
          status: assignmentStatus,
        }
        return updateAssignment(Object.assign(data, assignmentData))
          .unwrap()
          .then(() => goClientHome())
      }
      return setAssignment({
        ...assignmentProviderPayload,
        patient_id: clientDetails?.id as string,
        content_id: contentId,
        content_meta_data: contentMetadata,
        instructions: values.instructions,
        recipient_id: normalizedRecipientLyraId,
        session_period: sessionPeriod,
      })
        .unwrap()
        .then((assignment) => {
          // If the currentAssignmentResponse is a draft started by the Provider then we need to update the
          // assignment response with the Exercise id so the draft can be associated with the Exercise.
          if (isCurrentResponseProviderDraft) {
            const updatedResponse = Object.assign(cloneDeep(currentAssignmentResponse), {
              assignment_id: assignment.id,
            })
            dispatch(setResponseDetails(updatedResponse as AssignmentResponse))
            setAssignmentDraftResponseToAssignment(updatedResponse)
              .unwrap()
              .then(() => {
                dispatch(clearResponseDetails())
                navigateAfterAssignmentCreation()
              })
          } else {
            goClientHome()
          }
        })
    } else if (isExerciseForFutureSessionWithProviderDraft && !assignmentId) {
      const sessionToUpdate = curriculumCopy.findIndex((sessionObject) => sessionObject.session === selectedSession)

      // If the Exercise has a Provider draft and the Exercise is being added to a future Session,
      // we need to create the Assignment, add the Provider draft to it and update the curriculum
      return setAssignment({
        ...assignmentProviderPayload,
        patient_id: clientDetails?.id as string,
        content_id: contentId,
        content_meta_data: contentMetadata,
        instructions: values.instructions,
        recipient_id: normalizedRecipientLyraId,
        session_period: sessionPeriod,
      })
        .unwrap()
        .then((assignment) => {
          const updatedResponse = Object.assign(cloneDeep(currentAssignmentResponse), {
            assignment_id: assignment.id,
          })
          dispatch(setResponseDetails(updatedResponse as AssignmentResponse))
          return setAssignmentDraftResponseToAssignment(updatedResponse).unwrap()
        })
        .then((response) => {
          const curriculumContent = {
            name,
            title,
            group,
            content_type: type,
            instructions: values.instructions,
            content_meta_data: contentMetadata,
            id: response.assignment_id,
            recipient_id: normalizedRecipientLyraId,
          }
          if (editMode) {
            assignmentToUpdate = curriculumCopy[sessionToUpdate].contents.findIndex(
              (assignment) =>
                assignment.name === name &&
                getEffectiveRecipientLyraId(assignment.recipient_id, patientRecipientLyraId) ===
                  normalizedRecipientLyraId,
            )
            curriculumCopy[sessionToUpdate].contents[assignmentToUpdate] = curriculumContent
          } else {
            curriculumCopy[sessionToUpdate].contents.push(curriculumContent)
          }

          return updateCurriculum({ id: episodeId!, curriculum: curriculumCopy }).unwrap()
        })
        .then(() => {
          dispatch(clearResponseDetails())
          navigateAfterAssignmentCreation()
        })
    } else {
      // If we are editing a future Session Assignment with an ID we want to update it and not touch the Curriculum
      if (editMode && assignmentId) {
        const assignmentData = {
          id: assignmentId,
          status: assignmentStatus,
          session_period: selectedSession,
        }
        return updateAssignment(Object.assign(data, assignmentData))
          .unwrap()
          .then(() => goClientHome())
      }

      // if the session is in the future, update the curriculum for the corresponding session
      const sessionToUpdate = curriculumCopy.findIndex(
        (sessionObject: any) => sessionObject.session === selectedSession,
      )

      if (editMode) {
        if (sessionToUpdate > -1) {
          assignmentToUpdate = curriculumCopy[sessionToUpdate].contents.findIndex(
            (assignment) =>
              assignment.name === name &&
              getEffectiveRecipientLyraId(assignment.recipient_id, patientRecipientLyraId) ===
                normalizedRecipientLyraId,
          )
        }
        curriculumCopy[sessionToUpdate].contents[assignmentToUpdate].content_meta_data = contentMetadata
        curriculumCopy[sessionToUpdate].contents[assignmentToUpdate].instructions = values.instructions
        curriculumCopy[sessionToUpdate].contents[assignmentToUpdate].recipient_id = normalizedRecipientLyraId
      } else {
        curriculumCopy[sessionToUpdate].contents.push({
          name,
          title,
          group,
          content_type: type,
          instructions: values.instructions,
          content_meta_data: contentMetadata,
          content_id: contentId,
          recipient_id: normalizedRecipientLyraId,
        })
      }
      if (assignmentId) {
        unsetAssignment({ id: assignmentId })
      }
      return updateCurriculum({ id: episodeId!, curriculum: curriculumCopy })
        .unwrap()
        .then(() => {
          goClientHome()
        })
    }
  }

  const isLoading = !clientDetails || !curriculum
  if (!clientDetails || isEmpty(clientDetails)) navigate(LT_CLIENTS.route)

  const isFullScreenOverlayOpen = (group === 'exercise' || group === 'lesson') && showingPreview && !!modalContent
  const Container = View
  const videoSessionBannerHeight = useVideoSessionBannerHeight()

  return (
    <div style={{ marginTop: '30px' }}>
      <Container>
        <div style={{ display: 'flex' }}>
          <MiniBackNav backFunc={() => navigate(-1)} subText='' />
          {isLoading ? (
            <div className={styles['loading-container']}>
              <LoadingIndicator size={45} />
            </div>
          ) : (
            <div className={styles['details-card']}>
              <Form
                onSubmit={(values) => validateSelectedSession(values)}
                initialValues={initialValues}
                render={({ form, handleSubmit, submitting, values }) => {
                  return (
                    <>
                      <div className={styles.header}>
                        <h1>{title}</h1>
                        <div className={styles['button-container']}>
                          <DefaultButton
                            type='button'
                            isOutlined
                            onClick={() => onPreviewClick(values)}
                            data-test-id='ConfigAssignmentContainer-preview'
                          >
                            Preview activity
                          </DefaultButton>
                          <DefaultButton
                            data-test-id={`ConfigAssignmentContainer-${editMode ? `UpdateActivity` : `AddActivity`}`}
                            disabled={!assignmentProviderPayload || submitDisabled(values)}
                            isLoading={submitting}
                            type='submit'
                            onClick={() => handleSubmit()}
                            onKeyDown={() => handleSubmit()}
                          >
                            {editMode ? 'Update activity' : 'Add activity'}
                          </DefaultButton>
                        </div>
                      </div>
                      {renderConfigView(form, values)}
                    </>
                  )
                }}
              />
            </div>
          )}
        </div>
      </Container>
      {isFullScreenOverlayOpen ? (
        <FullScreenOverlay
          isOpen={isFullScreenOverlayOpen}
          isFocused={false}
          customStyles={videoSessionOpen ? { top: `${videoSessionBannerHeight}px`, zIndex: '10' } : {}}
          renderInPortal
          doNotHideAppRoot={videoSessionOpen}
        >
          {modalContent}
        </FullScreenOverlay>
      ) : (
        <BaseModal
          isOpen={!!modalContent}
          body={modalContent ?? <div />}
          closeModal={() => {
            setModalContent(null)
            setShowingPreview(false)
          }}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentSessionCount: getClientCurrentSessionCount(state) || 0,
    contents: getLyraTherapyContentsData(state) || [],
    videoSessionOpen: getLTVideoSessionOpen(state),
    currentAssignmentResponse: getClientAssignmentResponse(state),
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    actions: bindActionCreators(
      {
        setToastContent,
      },
      dispatch,
    ),
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(ConfigAssignmentContainer)
